import React, { useRef, useState } from 'react';
import { ALL_SOURCES, AvTooltip, SwitchToggle } from '@fabric-ui/components';
import { ArrowDown, Circle, QuestionFull, Unified } from '@fabric-ui/icons';
import { InfoFull as Warning } from '@fabric-ui/icons/colorful';
import { flex } from '@fabric-ui/theme';
import { emptyObject, iconSize } from '@fabric-ui/utils';
import { Box, ClickAwayListener, Divider, InputLabel, Paper, Popper, SxProps } from '@mui/material';
import useGetAccountSources from '../../hooks/useGetAccountSources';
import AvItemWithLogoGroup, { IconSize } from '../AvItemWithLogoGroup';
import DataSourceSelect from '../DataSourceSelect';
import { IconVariant } from '../ItemWithLogo';

const popupIcon = <ArrowDown style={iconSize(16)} />;
const questionFull = <QuestionFull />;
const unified = <Unified style={iconSize(20)} />;
const circle = <Circle style={iconSize(20)} />;
const warning = <Warning />;

interface UnifiedIngressToggleProps {
  isIngressExp: boolean;
  onChangeIngressExp: () => void;
  onChangeSources: (source) => void;
  sourcesValue: (string | typeof ALL_SOURCES)[];
  activeProjName?: string;
  enableAllSources?: boolean;
  sx?: SxProps;
  label?: string;
  showToggle?: boolean;
  multiSelect?: boolean;
  isIconCollapsed?: boolean;
}

const paddingY = 8;

const UnifiedIngressToggle: React.FC<UnifiedIngressToggleProps> = ({
  isIngressExp,
  onChangeIngressExp,
  onChangeSources,
  sourcesValue,
  activeProjName = 'uber_assets',
  enableAllSources = false,
  sx = emptyObject,
  label,
  showToggle = true,
  multiSelect = true,
  isIconCollapsed = true,
}) => {
  const { dataSources: availableSourceOption = [], isLoadingDataSources: isLoadingDetectionSources } = useGetAccountSources({
    projName: activeProjName,
  });

  const [isPopperOpen, setIsPopperOpen] = useState<boolean>(false);
  const boxRef = useRef(undefined);
  const warningIcon = <Box sx={{ ...flex.justifyEndCenter, ml: 9.5 }}>{warning}</Box>;
  return (
    <div>
      {label && <InputLabel required>{label}</InputLabel>}
      <Box sx={{ ...flex.itemsCenter }}>
        <Box
          onClick={() => setIsPopperOpen(prev => !prev)}
          ref={boxRef}
          sx={{
            ...flex.itemsCenter,
            border: '1px solid var(--color-border-interactive-secondary-default)',
            borderRadius: 2.5,
            pr: 1.5,
            height: 32,
            width: isIconCollapsed ? 144 : 200,
            transition: theme => theme.transitions.create(['border-color', 'background-color']),
            borderColor: isPopperOpen ? 'var(--color-border-interactive-primary-default)' : undefined,
            ':hover': isPopperOpen
              ? undefined
              : {
                  borderColor: 'var(--color-border-interactive-secondary-hover)',
                  backgroundColor: 'var(--color-surface-field-hover)',
                },
            ...sx,
          }}>
          <Box
            sx={{
              ...flex.center,
              backgroundColor: 'var(--color-surface-interactive-secondary-hover)',
              borderBottomLeftRadius: 9,
              borderTopLeftRadius: 9,
              height: '100%',
              width: 40,
              mr: 0.5,
              px: 0.7,
              color: 'var(--color-content-immutable-white)',
            }}>
            {isIngressExp ? circle : unified}
          </Box>
          <Box
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              width: '100%',
            }}>
            {isIngressExp ? (
              <AvItemWithLogoGroup
                variant={IconVariant.sourcesMapByName}
                items={sourcesValue.includes(ALL_SOURCES) ? availableSourceOption.map(({ name }) => name) : sourcesValue}
                size={isIconCollapsed ? IconSize.collapsed : IconSize.small}
                limit={1}
              />
            ) : (
              <Box sx={{ ml: 0.5, color: 'var(--color-content-base-tertiary)' }}>Unified</Box>
            )}
          </Box>
          <Box
            sx={{
              ...flex.center,
              mt: '1px',
              width: 14,
              height: 14,
              p: '12px',
              borderRadius: 2.5,
              transform: isPopperOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              textOverflow: 'clip',
              color: 'var(--color-content-base-secondary)',
              ':hover': {
                cursor: 'pointer',
                background: 'var(--color-surface-base-secondary)',
              },
            }}>
            {popupIcon}
          </Box>
        </Box>
        <Popper open={isPopperOpen} anchorEl={boxRef.current} placement="bottom-start" sx={{ zIndex: theme => theme.zIndex.modal }}>
          <ClickAwayListener onClickAway={() => setIsPopperOpen(prev => !prev)}>
            <Paper
              sx={{
                ...flex.colItemsStart,
                padding: 1,
                minWidth: 300,
                borderRadius: 1,
                gap: 1.5,
              }}>
              <Box
                sx={{
                  backgroundColor: 'var(--color-surface-severity-none-primary)',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  ...flex.justifyStartCenter,
                  gap: 1,
                  width: '100%',
                  height: 36,
                  fontSize: 12,
                  fontWeight: 600,
                  color: 'var(--color-content-base-secondary)',
                }}>
                <AvTooltip
                  title={
                    isIngressExp ? 'The field values from specific data sources' : 'The field value following the merge of multiple sources'
                  }>
                  <div>{questionFull}</div>
                </AvTooltip>
                {isIngressExp ? 'Original Source Value' : 'Merged Entity Value'}
              </Box>
              {showToggle && (
                <Box sx={{ ...flex.justifyBetweenCenter, width: '100%', px: 1.5 }}>
                  <Box sx={{ ...flex.row }}>
                    <SwitchToggle value={isIngressExp} onChange={onChangeIngressExp} sx={{ marginRight: 2, mb: '2px' }} /> Use Original
                    Source
                  </Box>
                  {isIngressExp && <AvTooltip title="You must choose at least one source">{warningIcon} </AvTooltip>}
                </Box>
              )}
              {showToggle && isIngressExp && (
                <Box sx={{ ...flex.center, width: '100%', px: 1.5 }}>
                  <Divider orientation="horizontal" sx={{ width: '100%' }} />
                </Box>
              )}
              {isIngressExp && (
                <Box
                  sx={{
                    minWidth: 284,
                    borderRadius: 1,
                  }}>
                  <DataSourceSelect
                    size="xSmall"
                    onChange={val => {
                      const newVal = enableAllSources
                        ? !sourcesValue.includes(ALL_SOURCES) && val.includes(ALL_SOURCES)
                          ? [ALL_SOURCES]
                          : sourcesValue.includes(ALL_SOURCES) && !val.includes(ALL_SOURCES)
                            ? []
                            : val.filter(source => source !== ALL_SOURCES)
                        : val;
                      onChangeSources(newVal);
                      if (!multiSelect) {
                        setIsPopperOpen(prev => !prev);
                      }
                    }}
                    options={
                      enableAllSources
                        ? [{ value: ALL_SOURCES, title: 'Any Original Source' }, ...availableSourceOption]
                        : availableSourceOption
                    }
                    filterValue={
                      enableAllSources && sourcesValue.includes(ALL_SOURCES)
                        ? [ALL_SOURCES, ...availableSourceOption.map(({ name }) => name)].filter(a => a)
                        : multiSelect
                          ? sourcesValue
                          : sourcesValue[0]
                    }
                    selectProps={{
                      showOnlyAutoComplete: true,
                      loading: isLoadingDetectionSources,
                      isMultiple: multiSelect,
                      muiProps: {
                        sx: { width: '100%', px: 1.5 },
                        slotProps: {
                          paper: {
                            sx: { boxShadow: 'unset', '.MuiAutocomplete-listbox': { maxHeight: 200 - 2 * paddingY } },
                          },
                        },
                      },
                    }}
                  />
                </Box>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Box>
    </div>
  );
};

export default UnifiedIngressToggle;
